import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  jobPayloadMatchesSignal,
  type TTreatmentPlanJob,
  type TTreatmentPlanJobSignal,
} from "@/api/models/treatmentPlan/job";

export type TJobSource = "main" | "backLoop";

export const useTreatmentPlanStore = defineStore(
  "treatmentPlan",
  () => {
    const availableJobsBySource = ref<Record<TJobSource, TTreatmentPlanJob[]>>({
      main: [],
      backLoop: [],
    });

    const availableJobs = computed(() => {
      return [
        ...(availableJobsBySource.value.main || []),
        ...(availableJobsBySource.value.backLoop || []),
      ];
    });

    /**
     * Register jobs for a specific source
     * This is a replacement of the setAvailableJobs method, to have a better handle on the changes of the jobs
     * @param source - The source of the jobs (e.g. "main", "backLoop")
     * @param jobs - The jobs to register
     */
    const registerJobs = (source: TJobSource, jobs: TTreatmentPlanJob[] | undefined) => {
      availableJobsBySource.value[source] = jobs ?? [];
    };

    const getAvailableJob = (jobSignal: TTreatmentPlanJobSignal) =>
      computed(
        () => availableJobs.value.find((job) => jobPayloadMatchesSignal(job, jobSignal)) || null
      );

    const getAvailableJobId = (jobSignal: TTreatmentPlanJobSignal) =>
      computed(() => getAvailableJob(jobSignal).value?.id || undefined);

    return {
      availableJobsBySource,
      availableJobs,
      registerJobs,
      getAvailableJob,
      getAvailableJobId,
    };
  },
  {
    persist: true,
  }
);
